import Vue from 'vue';
import Modal from 'components/modules/accordions-modals/modals/modal/modal.mixin';
import {KeyCodes, Events, Intervals} from 'utils/Constants';
import ViewportHelper from 'utils/ViewportHelper';
import {TweenMax, Power1, ScrollToPlugin} from 'gsap/all';
import Throttle from 'utils/Throttle';

/**
 * A reference to the refs used in this view
 *
 * @property REFS
 * @type {Object}
 * @private
 */
const REFS = {
  'MODAL_CONTENT': 'js-modal-content',
  'MODAL_TITLE': 'js-modal-title',
  'MODAL_META': 'js-modal-meta'
};

/**
 * A reference to the classes used in this view
 *
 * @property CLASSES
 * @type {Object}
 * @private
 */
const CLASSES = {
  'TRIGGER_ACTIVE': 'definition_active'
};

/**
 * MAGIC_NUMBERS a reference to the variables used for modal positioning
 * @type {Object}
 */
const MAGIC_NUMBERS = {
  'HORIZONTAL_MARGIN': 40,
  'VERTICAL_MARGIN': 96,
  'TOOLTIP_TOP_OFFSET': 80
};


Vue.component('tooltip-vue', {
  mixins: [Modal],

  mounted() {
    this.$modal = $(this.$refs.modal);
    this.$window = $(window);
    this.$trigger.on(Events.KEYDOWN, e => this.handleTriggerKeydown(e));

    const scrollCallback = Throttle(e => this.handleScroll(e), Intervals.SCROLL_INTERVAL);
    this.$window.on(Events.SCROLL, scrollCallback);

    const resizeCallback = Throttle(e => this.resizeModal(), Intervals.RESIZE_INTERVAL);
    this.$window.on(Events.RESIZE, resizeCallback);
  },

  methods: {
    /**
     * handleScroll handles the scroll event, close tooltip when it is off the page
     */
    handleScroll() {
      if (!this.scrollTween || !this.scrollTween.isActive()) {
        this.allowFocusPrevious = false;
        if (this.isModalOffscreen()) {
          this.isExpanded = false;
        }
      }
    },

    /**
     * isModalOffscreen determines if modal is offscreen enough to close
     * @return {Boolean}
     */
    isModalOffscreen() {
      const measurements = this.getMeasurements();
      const modalTop = this.$modal.offset().top;

      return this.$window.scrollTop() - modalTop > MAGIC_NUMBERS.VERTICAL_MARGIN
      || modalTop + measurements.modalHeight - measurements.scrollBottom > MAGIC_NUMBERS.VERTICAL_MARGIN;
    },

    /**
     * handleTriggerKeydown will determine if enter is pressed and open tooltip
     * @param  {event} e keydown event
     * @return {void}
     */
    handleTriggerKeydown(e) {
      if (e.keyCode === KeyCodes.ENTER) {
        e.preventDefault();
        this.handleTriggerClick(e);
        this.allowFocusPrevious = true;
      }
    },

    /**
     * handleTriggerClick handles the click of a trigger, either inside the module or anywhere on page.
     * Shows and hides the modal and supports swapping the content in the modal to specified content on the trigger using data-modal-content/title
     * @param  {event} e the event
     * @return {void}
     */
    handleTriggerClick(e) {
      this.$lastTrigger = $(e.target);

      if (this.$lastTrigger.hasClass(CLASSES.TRIGGER_ACTIVE) && this.isExpanded) {
        this.resetActiveStates();
        this.isExpanded = false;
        return;
      }

      this.resetActiveStates();
      this.$lastTrigger.addClass(CLASSES.TRIGGER_ACTIVE);

      const content = e.target.getAttribute('data-modal-content');
      const title = e.target.getAttribute('data-modal-title');
      const meta = e.target.getAttribute('data-modal-meta');

      //let's require title and content for dynamic modals so we don't have to worry about blanking out a title or content
      if (!this.isExpanded && content.length > 0) {
        this.$refs[REFS.MODAL_CONTENT].innerHTML = content;
        this.$refs[REFS.MODAL_TITLE].innerHTML = title;
        this.$refs[REFS.MODAL_META].innerHTML = meta;
      }

      if (this.positionRelativeTrigger && ViewportHelper.isTablet())
        this.positionModal(this.$lastTrigger);

      if (!this.isExpanded)
        this.isExpanded = !this.isExpanded;
    },

    /**
     * positionModal will position a modal (tooltip) based on the trigger that activated the modal
     * @param  {DOM Element} trigger DOM trigger
     * @return {void}
     */
    positionModal() {
      if (ViewportHelper.isTablet()) {
        const coords = this.getModalPosition(this.$lastTrigger);

        TweenMax.fromTo($(this.$refs.modal), .2, {
          top: `${coords.start.top}px`,
          right: `${coords.start.right}px`
        },
        {
          top: `${coords.end.top}px`,
          right: `${coords.end.right}px`,
          'ease': Power1.easeOut
        });
      }
    },

    /**
     * resizeModal move modal when resizing
     * @return {void}
     */
    resizeModal() {
      this.isExpanded = false;
      this.resetActiveStates();

      if (ViewportHelper.isMobile()) {
        this.$refs.modal.style.top = 0;
        this.$refs.modal.style.right = 0;
      }
    },

    /**
     * getModalPosition will get the start and ending values for the top and right of the modal for animations
     * based on various contraints such as viewport modal height and the position on the page in relation to scroll position
     * @return {object}          an object with the animations parameters
     */
    getModalPosition() {
      const triggerOffset = this.$lastTrigger.offset();
      const measurements = this.getMeasurements(this.$modal);

      const coords = this.getDefaults(measurements.windowWidth, triggerOffset, measurements.modalWidth);

      if (!ViewportHelper.isTablet()) { return coords; }

      let $widthElement = this.getContainer(this.$modal);

      //if top is too high, place it visibly in screen without scrolling up
      if (coords.end.top < measurements.windowScrollTop) {
        coords.end.top = measurements.windowScrollTop + MAGIC_NUMBERS.VERTICAL_MARGIN;
      }
      //if modal can't fully be seen and it will fit in the viewport, scroll down
      else if ((measurements.modalHeight < measurements.windowHeight) && (measurements.modalHeight + coords.end.top > measurements.scrollBottom)) {
        const position = measurements.windowScrollTop + MAGIC_NUMBERS.VERTICAL_MARGIN + (measurements.modalHeight - (measurements.scrollBottom - coords.end.top));
        this.scrollTween = TweenMax.to(this.$window, 1, {
          scrollTo: position
        });
      }

      const possibleRight = measurements.windowWidth - coords.end.right - $widthElement.width() - $widthElement.offset().left - measurements.modalWidth;

      //if modal will fit in right margin place it there
      if (possibleRight > MAGIC_NUMBERS.HORIZONTAL_MARGIN) {
        coords.end.right = possibleRight;
        coords.end.top = triggerOffset.top - MAGIC_NUMBERS.TOOLTIP_TOP_OFFSET;
      }

      return coords;
    },

    /**
     * getMeasurements returns common measurements used to calculate positioning of modal
     * @return {object} an object with measurements
     */
    getMeasurements() {
      const measurements = {
        modalWidth: this.$modal.width(),
        windowWidth: this.$window.width(),
        modalHeight: this.$modal.height(),
        windowHeight: this.$window.height(),
        windowScrollTop: this.$window.scrollTop()
      };

      measurements.scrollBottom = measurements.windowScrollTop + measurements.windowHeight;
      return measurements;
    },

    /**
     * getDefaults gets the default coords for animation
     * @param  {int} windowWidth   window width
     * @param  {jquery element} triggerOffset the jquery trigger offset
     * @param  {int} modalWidth    the width of the modal
     * @return {void}               description
     */
    getDefaults(windowWidth, triggerOffset, modalWidth) {
      const startTop = triggerOffset.top + MAGIC_NUMBERS.VERTICAL_MARGIN;

      return {
        'start': {
          'top': startTop,
          'right': windowWidth - triggerOffset.left - (modalWidth / 2)
        },
        'end': {
          'top': startTop,
          'right': MAGIC_NUMBERS.HORIZONTAL_MARGIN
        }
      }
    },

    /**
     * getContainer will return the container of the trigger for determining modal placement. Looks for container and defaults to whatever is beside the modal
     * @param  {jquery element} $modal the modal
     * @return {jquery element}        the container
     */
    getContainer($modal) {
      let $container = this.$lastTrigger.parents('.container').find(':first-child').first();

      if ($container.length < 1) {
        $container = $modal.prev();
      }

      return $container;
    },

    /**
     * resetActiveStates remove active definition styles
     */
    resetActiveStates() {
      $(`.${CLASSES.TRIGGER_ACTIVE}`).removeClass(CLASSES.TRIGGER_ACTIVE);
    }
  }
});
